export interface ChatNotificationMute {
    scope: 'GLOBAL' | 'ROOM';
    roomId: string | null;
    mutedUntil: string | null;
}

const activeRoomMutes = new Map<string, ChatNotificationMute>();
let activeGlobalMute: ChatNotificationMute | null = null;
const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;

export const isChatNotificationMuteActive = (
    mute: ChatNotificationMute,
    now: number = Date.now(),
): boolean => mute.mutedUntil === null || Date.parse(mute.mutedUntil) > now;

export const replaceActiveChatNotificationMutes = (mutes: readonly ChatNotificationMute[]): void => {
    activeRoomMutes.clear();
    activeGlobalMute = null;
    const now = Date.now();

    mutes.filter((mute) => isChatNotificationMuteActive(mute, now)).forEach((mute) => {
        if (mute.scope === 'GLOBAL') {
            activeGlobalMute = mute;
        } else if (mute.roomId) {
            activeRoomMutes.set(mute.roomId, mute);
        }
    });
};

export const setActiveRoomChatNotificationMute = (mute: ChatNotificationMute): void => {
    if (mute.roomId && isChatNotificationMuteActive(mute)) {
        activeRoomMutes.set(mute.roomId, mute);
    }
};

export const removeActiveRoomChatNotificationMute = (roomId: string): void => {
    activeRoomMutes.delete(roomId);
};

export const isRoomChatNotificationMuted = (roomId: string): boolean => {
    if (activeGlobalMute) {
        if (isChatNotificationMuteActive(activeGlobalMute)) return true;
        activeGlobalMute = null;
    }

    const roomMute = activeRoomMutes.get(roomId);
    if (!roomMute) return false;
    if (isChatNotificationMuteActive(roomMute)) return true;

    activeRoomMutes.delete(roomId);
    return false;
};

export const nextChatNotificationMuteRefreshDelay = (
    mutes: readonly ChatNotificationMute[],
    now: number = Date.now(),
): number | null => {
    const expiryDelays = mutes
        .map((mute) => mute.mutedUntil)
        .filter((mutedUntil): mutedUntil is string => mutedUntil !== null)
        .map((mutedUntil) => Date.parse(mutedUntil) - now)
        .filter((delay) => Number.isFinite(delay) && delay > 0);

    return expiryDelays.length === 0
        ? null
        : Math.min(Math.min(...expiryDelays), MAX_TIMEOUT_DELAY_MS);
};
